// ============================================
// FILE: src/components/modals/EditAddressModal.jsx
// ============================================
//
// The lightweight "change where I'm delivering" form — address text plus
// pincode, backed by the parent's autocomplete. Restyled only; the props,
// validation and setUserProfile payload are unchanged.

import React, { useState } from 'react';
import { X, MapPin, ArrowLeft, Home as HomeIcon } from 'lucide-react';

const EditAddressModal = ({
  isOpen,
  onClose,
  userProfile,
  setUserProfile,
  searchAddress,
  addressSuggestions,
  showAddressSuggestions,
  selectAddress,
  showToast,
  editAddress,
}) => {
  const [localAddress, setLocalAddress] = useState(editAddress?.address || userProfile.address || '');
  const [localPincode, setLocalPincode] = useState(editAddress?.pincode || userProfile.pincode || '');

  if (!isOpen) return null;

  const handleAddressChange = (value) => {
    setLocalAddress(value);
    searchAddress(value);
  };

  const handleSelectSuggestion = (suggestion) => {
    setLocalAddress(suggestion.address);
    setLocalPincode(suggestion.pincode);
    selectAddress(suggestion);
  };

  const handleSaveAddress = () => {
    if (!localAddress.trim()) {
      showToast('Please enter your address', 'warning');
      return;
    }

    if (!localPincode.trim() || localPincode.length !== 6) {
      showToast('Please enter a valid 6-digit pincode', 'warning');
      return;
    }

    setUserProfile({
      ...userProfile,
      address: localAddress,
      pincode: localPincode,
    });

    showToast('Address updated successfully!', 'success');
    onClose();
  };

  return (
    <div className="fashion-scope fixed inset-0 z-[310] bg-[#fafafa] overflow-y-auto">
      <div className="max-w-[560px] mx-auto min-h-full bg-white">

        {/* Header */}
        <div
          className="sticky top-0 z-10 bg-white flex items-center gap-3 px-4 py-3"
          style={{ borderBottom: '1px solid var(--fx-line)' }}
        >
          <button onClick={onClose} className="p-1 -ml-1" aria-label="Go back">
            <ArrowLeft size={22} style={{ color: 'var(--fx-ink)' }} />
          </button>
          <span className="fx-label flex-1">Edit Address</span>
          <button onClick={onClose} className="p-1 -mr-1" aria-label="Close">
            <X size={20} style={{ color: 'var(--fx-ink-2)' }} />
          </button>
        </div>

        <div className="px-4 py-5 pb-28">
          <h3
            className="mb-4"
            style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--fx-ink-2)' }}
          >
            Delivery Address
          </h3>

          {/* Address with autocomplete */}
          <div className="fx-field relative">
            <label htmlFor="edit-address">Address *</label>
            <textarea
              id="edit-address"
              rows={3}
              value={localAddress}
              onChange={(e) => handleAddressChange(e.target.value)}
              placeholder="Search for your address…"
              className="fx-input"
            />

            {showAddressSuggestions && addressSuggestions.length > 0 && (
              <div
                className="absolute z-20 w-full mt-2 bg-white max-h-60 overflow-y-auto hide-scrollbar"
                style={{ border: '1px solid var(--fx-line)', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
              >
                {addressSuggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSelectSuggestion(suggestion)}
                    className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-[#fafafa]"
                    style={{ borderBottom: '1px solid var(--fx-line)' }}
                  >
                    <MapPin size={16} style={{ color: 'var(--fx-ink-3)', marginTop: 2, flexShrink: 0 }} />
                    <span>
                      <span className="block" style={{ fontSize: 14, color: 'var(--fx-ink)' }}>
                        {suggestion.address}
                      </span>
                      <span className="block" style={{ fontSize: 12, color: 'var(--fx-ink-3)' }}>
                        Pincode: {suggestion.pincode}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Pincode */}
          <div className="fx-field">
            <label htmlFor="edit-pincode">Pin Code *</label>
            <input
              id="edit-pincode"
              type="text"
              inputMode="numeric"
              value={localPincode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                if (value.length <= 6) setLocalPincode(value);
              }}
              placeholder="6-digit pincode"
              maxLength={6}
              className="fx-input"
            />
          </div>

          {/* Current address on file, for reference */}
          {userProfile.address && (
            <div
              className="p-4 mt-2"
              style={{ border: '1px solid var(--fx-line)', borderRadius: 4, background: '#fafafa' }}
            >
              <div className="flex items-start gap-3">
                <HomeIcon size={17} style={{ color: 'var(--fx-ink-2)', marginTop: 2, flexShrink: 0 }} />
                <div className="min-w-0">
                  <p className="fx-label" style={{ fontSize: 12 }}>Current Address</p>
                  <p className="mt-1" style={{ fontSize: 13, color: 'var(--fx-ink-2)', lineHeight: 1.5 }}>
                    {userProfile.address}
                  </p>
                  {userProfile.pincode && (
                    <p style={{ fontSize: 13, color: 'var(--fx-ink-2)' }}>
                      Pincode:{' '}
                      <span style={{ fontWeight: 700, color: 'var(--fx-ink)' }}>{userProfile.pincode}</span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <p className="mt-5" style={{ fontSize: 12, color: 'var(--fx-ink-3)', lineHeight: 1.6 }}>
            Make sure your address is correct so we can deliver on time. You can update it anytime.
          </p>
        </div>

        {/* Pinned actions */}
        <div
          className="fixed inset-x-0 bottom-0 bg-white flex gap-3 px-4 py-3 max-w-[560px] mx-auto"
          style={{ borderTop: '1px solid var(--fx-line)' }}
        >
          <button type="button" onClick={onClose} className="fx-btn fx-btn-ghost flex-1">
            Cancel
          </button>
          <button type="button" onClick={handleSaveAddress} className="fx-btn fx-btn-primary flex-1">
            Save Address
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditAddressModal;
